import React, { useMemo } from 'react';
import moment from "moment-timezone";
import { Unit, roundOfAcres } from '../../helpers/unit';
import { SummaryRow, SummaryItem, SummaryBox } from './utils';
import { toDisplayTimeString } from '../../helpers/moment-ex';
import { isCrewView, isCustomerView, isEGView, isFarmView } from '../../helpers/common';
import { DISPLAY_DATE_FORMAT2, LOGISTIC_PLAN_TYPE_ID, PLANTING_PLAN_TYPE_ID } from '../../constants';
import { DisplayDateRange } from '../widgets/Date';
import { AnalyzeSeedAccordian } from './AnalyzeSeedAccordian';

const SummaryOne = props => {
    const unit = Unit.getInstance();
    const { params: { view, isHarvester, isAutomaticLoad, regionId }, settings, workedKPI, summary: d, speed, donutInfo,
        isRequesting, totalLoadCapacity, selectedPlanTypeId, dataBySeedVariety, regionTypeId } = props;
    const isManualLoad = !isAutomaticLoad;

    const isRequestingPlanned = d.isRequesting;
    const isRequestingWorked = d.isRequesting ? true : workedKPI.isRequesting;
    const isRequestingLoad = isRequestingWorked;
    const isRequestingSpeed = speed.isRequesting;

    const plannedAcres = unit.toRoundOfArea(d.acres.planned);
    const completedAcres = unit.toRoundOfArea(d.acres.completed);
    const remainingAcres = (plannedAcres > completedAcres) ? roundOfAcres(plannedAcres - completedAcres) : 0;
    const completedArea = unit.toArea(d.acres.completed);
    const avgYieldPerAcre = (d.load && d.load.total_capacity >= 0 && completedArea > 0) ?
        unit.toRoundOfWeight(d.load.total_capacity / completedArea) : 0;
    const yieldByTruck = isCrewView(view) && isManualLoad && d.load
        && d.load.total_capacity >= 0 && d.load.total_loads > 0
        ? unit.toRoundOfWeight(d.load.total_capacity / d.load.total_loads) : 0;
    const totalTareWeight = (d && d.tare && d.tare.total_capacity) ? unit.toRoundOfWeight(d.tare.total_capacity, false) : 0;
    const totalLoadWeightUnits = d && d.load && d.load.total_capacity ? unit.toRoundOfWeight(d.load.total_capacity, false) : 0;
    const totalWeigthIn = (d && d.load && d.load.weight_in) ? unit.toRoundOfWeight(d.load.weight_in, false) : 0;
    const totalWeigthOut = (d && d.load && d.load.weight_out) ? unit.toRoundOfWeight(d.load.weight_out, false) : 0;

    const regiontotal = unit.toArea(d.region.total, 2);
    const regioncompleted = unit.toArea(d.region.completed, 2) > regiontotal ? regiontotal : unit.toArea(d.region.completed, 2);
    const regionremaining = unit.toArea(d.region.remaining, 2);
    const regionAvgYieldPerAcre = (d.load && d.load.total_capacity >= 0 && regioncompleted > 0) ?
        unit.toRoundOfWeight(d.load.total_capacity / regioncompleted) : 0;
    const weightShortName = settings ? settings.weightShortName.charAt(0).toUpperCase() + settings.weightShortName.slice(1) : 'Capacity';
    const totalWeight = unit.toFixedOf(totalLoadCapacity + totalTareWeight, 2);
    const isTareIncludedLoad = (d && d.tare && d.tare.is_tare_included_load);
    const tareLoad = (d && d.tare && d.tare.total_loads > 0);
    const averageCapacityPerHour = useMemo(() => {
        const { load } = d;
        const { actual_work } = donutInfo;

        if (!load || !actual_work || !actual_work.hasOwnProperty('h') || !actual_work.hasOwnProperty('m')) return 0;

        const totalHoursWorked = actual_work.h + (actual_work.m / 60);
        const averageCapacityPerHour = totalHoursWorked > 0 ? load.total_capacity / totalHoursWorked : 0;

        return unit.toRoundOfWeight(averageCapacityPerHour);
    }, [d, isRequestingLoad, donutInfo]);
    // regionTypeId === 4 refers to offload site
    const logisticTotalCapacity = d.load.withdrawn_weights ? unit.toRoundOfWeight(d.load.withdrawn_weights).toFixed(2) : 0;
    const seedOutCapacity = d.load.weight_out ? unit.toRoundOfWeight(d.load.weight_out).toFixed(2) : 0;
    const isNotLogisticPlanType = selectedPlanTypeId !== LOGISTIC_PLAN_TYPE_ID;
    const isLogisticPlanType = selectedPlanTypeId === LOGISTIC_PLAN_TYPE_ID;
    const isSeedPlant = selectedPlanTypeId === PLANTING_PLAN_TYPE_ID;
    return <div>
        {isCrewView(view) &&
            <div className="text-center plan-date">
                {isRequestingPlanned && <span className="text-label">Loading...</span>}
                {!isRequestingPlanned
                    && d.plan_start_date
                    && d.plan_end_date
                    && moment.utc(d.plan_end_date).year() >= 2000 ?
                    <>
                        <span className="text-label">Planned :</span>
                        <DisplayDateRange
                            start={moment.utc(d.plan_start_date).format(DISPLAY_DATE_FORMAT2)}
                            end={moment.utc(d.plan_end_date).format(DISPLAY_DATE_FORMAT2)}
                        />
                    </>
                    : !isRequestingPlanned ? <span className="text-label text-error">No work plan</span> : ""}
            </div>}
        {
            regionId ? <SummaryBox>
                {(regionTypeId !== 4) ? <SummaryRow>
                    <SummaryItem title={`Total ${settings.areaShortName}`} value={regiontotal} isLoading={isRequestingLoad} />
                    <SummaryItem title={`Completed ${settings.areaShortName}`} value={regioncompleted} isLoading={isRequestingLoad} />
                    <SummaryItem title={`Remaining ${settings.areaShortName}`} value={regionremaining} isLoading={isRequestingLoad} />
                </SummaryRow> : null}
                <SummaryRow>
                    <SummaryItem
                        title="Total Loads"
                        value={isSeedPlant ? d.load.withdrawn_loads ? d.load.withdrawn_loads : d.load.total_loads : d.load.total_loads}
                        isLoading={isRequestingLoad}
                    />
            
                    {isSeedPlant &&    <SummaryItem
                            title="Total Net Weight"
                            value={isSeedPlant ? logisticTotalCapacity ? logisticTotalCapacity : totalLoadWeightUnits : totalLoadWeightUnits}
                            unit={settings.weightShortName}
                            isLoading={isRequestingLoad}
                        /> }
                     
                    {((selectedPlanTypeId && selectedPlanTypeId !== 2) && (regionTypeId !== 4)) ?
                        <>
                            <SummaryItem
                                title="Total Net Weight"
                                unit={settings.weightShortName}
                                value={totalLoadCapacity}
                                isLoading={isRequesting}
                            />
                            <SummaryItem
                                title={`Avg Yield/${settings.areaShortName}`}
                                value={regionAvgYieldPerAcre}
                                unit={settings.weightShortName}
                                isLoading={isRequestingLoad}
                            />
                        </> : null}
                    {(regionTypeId !== 4) ?
                        <>
                            <SummaryItem
                                title="Avg Load Time"
                                value={toDisplayTimeString(d.load.avg_load_time)}
                                isLoading={isRequestingLoad}
                            />
                            <SummaryItem
                                title="Avg Roundtrip Load Time"
                                value={toDisplayTimeString(d.load.avg_roundtrip_load_time)}
                                isLoading={isRequestingLoad}
                            />
                        </> : null}
                    {
                        (regionTypeId !== 4) && d.region && d.region.offload && d.region.offload.length > 0 && d.region.offload[0].offload_geo_shape_uuid
                        && d.region.offload.map((offload, index) => (
                            <SummaryItem
                                key={index}
                                title={`${offload.offload_geo_shape_name} Loads`}
                                value={offload.load}
                                isLoading={isRequestingLoad}
                            />
                        ))
                    }
                    {
                        (!isTareIncludedLoad && tareLoad) ?
                            <SummaryItem
                                title="Total Tare Loads"
                                value={d && d.tare && d.tare.total_loads}
                                isLoading={isRequestingLoad}
                            /> : null
                    }

                    <SummaryItem
                        title="Total Tare Weight"
                        value={totalTareWeight}
                        unit={settings.weightShortName}
                        isLoading={isRequestingLoad}
                    />
                    {isSeedPlant ? <>
                        <SummaryItem
                            title="Withdrawn Weight"
                            value={seedOutCapacity}
                            isLoading={isRequestingLoad}
                            unit={settings.weightShortName}
                        />
                        <SummaryItem
                            title="Remaining Weight"
                            value={logisticTotalCapacity ? (logisticTotalCapacity - seedOutCapacity).toFixed(2) : 0}
                            isLoading={isRequestingLoad}
                            unit={settings.weightShortName}
                        />

                    </> : null}
                    {isNotLogisticPlanType && !isSeedPlant ?
                        <>
                            <SummaryItem
                                title="Withdrawn Weight"
                                value={logisticTotalCapacity}
                                isLoading={isRequestingLoad}
                                unit={settings.weightShortName}
                            />
                            <SummaryItem
                                title="Remaining Weight"
                                value={totalLoadWeightUnits ? (totalLoadCapacity - logisticTotalCapacity).toFixed(2) : 0}
                                isLoading={isRequestingLoad}
                                unit={settings.weightShortName}
                            />
                        </> : null
                    }

                    {(regionTypeId !== 4) ? <>
                        <SummaryItem
                            title="Avg Tare Load Time"
                            value={toDisplayTimeString(d && d.tare && d.tare.avg_load_time)}
                            isLoading={isRequestingLoad}
                        />
                        <SummaryItem
                            title="Avg Tare Roundtrip Load Time"
                            value={toDisplayTimeString(d && d.tare && d.tare.avg_roundtrip_load_time)}
                            isLoading={isRequestingLoad}
                        /></> : null}
                </SummaryRow>
                <div className='mt-1'>
                    {(selectedPlanTypeId && selectedPlanTypeId === 2) ?
                        <SeedVarietyRowItem dataBySeedVariety={dataBySeedVariety} isRequestingLoad={isRequestingLoad} settings={settings} />
                        : null}
                </div>

                {isLogisticPlanType ?
                    <SummaryRow>
                        <SummaryItem
                            title="Weight In"
                            value={totalWeigthIn}
                            isLoading={isRequestingLoad}
                            unit={settings.weightShortName}
                        />
                        <SummaryItem
                            title="Weight Out"
                            value={totalWeigthOut}
                            isLoading={isRequestingLoad}
                            unit={settings.weightShortName}
                        />
                    </SummaryRow> : null
                }
            </SummaryBox>
                : <SummaryBox>
                    {!isCustomerView(view) && d.acres && <SummaryRow>
                        <SummaryItem title={`Planned ${settings.areaShortName}`} value={plannedAcres} isLoading={isRequestingPlanned} />
                        <SummaryItem title={`Completed ${settings.areaShortName}`} value={completedAcres} isLoading={isRequestingWorked} />
                        <SummaryItem title={`Remaining ${settings.areaShortName}`} value={remainingAcres} isLoading={isRequestingWorked} />
                    </SummaryRow>}
                    {(!isFarmView(view) && !isCustomerView(view)) && d.hours && <SummaryRow>
                        <SummaryItem title="Planned Hours" value={toDisplayTimeString(d.hours.planned)} isLoading={isRequestingPlanned} />
                        <SummaryItem title="Completed Hours" value={toDisplayTimeString(d.hours.completed)} isLoading={isRequestingWorked} />
                        <SummaryItem title="Remaining Hours" value={toDisplayTimeString(d.hours.remaining)} isLoading={isRequestingWorked} />

                        {(!isCrewView(view) && selectedPlanTypeId && selectedPlanTypeId === 2) ?
                            <SeedVarietyRowItem dataBySeedVariety={dataBySeedVariety} isRequestingLoad={isRequestingLoad} settings={settings} />
                            : null}

                    </SummaryRow>}
                    {isFarmView(view) && d.crews && <SummaryRow>
                        <SummaryItem title="Total Crews" value={d.crews.planned} isLoading={isRequestingPlanned} />
                        <SummaryItem title="Active Crews" value={d.crews.active} isLoading={isRequestingPlanned} />
                        <SummaryItem title="Inactive Crews" value={d.crews.inactive} isLoading={isRequestingPlanned} />
                    </SummaryRow>}
                    {isFarmView(view) && d.load &&
                        <>
                            <SummaryRow>
                                <SummaryItem
                                    title="Total Loads"
                                    value={d.load.total_loads}
                                    isLoading={isRequestingLoad}
                                />
                                <SummaryItem
                                    title="Total Net Weight"
                                    unit={settings.weightShortName}
                                    value={totalLoadCapacity}
                                    isLoading={isRequesting}
                                />

                                {(selectedPlanTypeId && selectedPlanTypeId !== 2) ?
                                    <>
                                        <SummaryItem
                                            title={`Avg Yield/${settings.areaShortName}`}
                                            value={avgYieldPerAcre}
                                            unit={settings.weightShortName}
                                            isLoading={isRequestingLoad}
                                        />
                                        <SummaryItem
                                            title={`Avg ${weightShortName}/Hour`}
                                            value={averageCapacityPerHour}
                                            isLoading={donutInfo && donutInfo.isRequesting ? donutInfo.isRequesting : isRequestingLoad}
                                        />
                                        {
                                            !isTareIncludedLoad && tareLoad ?
                                                <SummaryItem
                                                    title="Total Tare Loads"
                                                    value={d && d.tare && d.tare.total_loads}
                                                    isLoading={isRequestingLoad}
                                                /> : null
                                        }
                                        <SummaryItem
                                            title="Total Tare Weight"
                                            value={totalTareWeight}
                                            unit={settings.weightShortName}
                                            isLoading={isRequestingLoad}
                                        />
                                        <SummaryItem
                                            title="Total Weight"
                                            value={totalWeight}
                                            unit={settings.weightShortName}
                                            isLoading={isRequestingLoad}
                                        />

                                        <SummaryItem
                                            title="Avg Tare Load Time"
                                            value={toDisplayTimeString(d && d.tare && d.tare.avg_load_time)}
                                            isLoading={isRequestingLoad}
                                        />
                                        <SummaryItem
                                            title="Avg Tare Roundtrip Load Time"
                                            value={toDisplayTimeString(d && d.tare && d.tare.avg_roundtrip_load_time)}
                                            isLoading={isRequestingLoad}
                                        />
                                        {isNotLogisticPlanType ? <>
                                            <SummaryItem
                                                title="Withdrawn Loads"
                                                value={d.load.withdrawn_loads}
                                                isLoading={isRequestingLoad}
                                            />
                                            {/* <SummaryItem
                                                title="Remaining Load"
                                                value={(d.load.total_loads - d.load.withdrawn_loads)}
                                                isLoading={isRequestingLoad}
                                            /> */}
                                        </> : null}

                                        {isNotLogisticPlanType ?
                                            <>
                                                <SummaryItem
                                                    title="Withdrawn Weight"
                                                    value={logisticTotalCapacity}
                                                    isLoading={isRequestingLoad}
                                                    unit={settings.weightShortName}
                                                />
                                                <SummaryItem
                                                    title="Remaining Weight"
                                                    value={(totalLoadCapacity - logisticTotalCapacity).toFixed(2)}
                                                    isLoading={isRequestingLoad}
                                                    unit={settings.weightShortName}
                                                />
                                            </> : null
                                        }
                                        {/* {isLogisticPlanType ?
                                            <>
                                                <SummaryItem
                                                    title="Weight In"
                                                    value={logisticTotalCapacity}
                                                    isLoading={isRequestingLoad}
                                                    unit={settings.weightShortName}
                                                />
                                                <SummaryItem
                                                    title="Weight Out"
                                                    value={(totalLoadCapacity - logisticTotalCapacity).toFixed(2)}
                                                    isLoading={isRequestingLoad}
                                                    unit={settings.weightShortName}
                                                />
                                            </> : null
                                        } */}

                                    </> : null
                                }
                            </SummaryRow>
                            <div className='mt-1'>
                                {(selectedPlanTypeId && selectedPlanTypeId === 2) ?
                                    <SeedVarietyRowItem dataBySeedVariety={dataBySeedVariety} isRequestingLoad={isRequestingLoad} settings={settings} />
                                    : null}
                            </div>
                        </>
                    }

                    {isCrewView(view) && isManualLoad && d.load &&
                        <>
                            <SummaryRow>
                                <SummaryItem
                                    title="Total Loads"
                                    value={d.load.total_loads}
                                    isLoading={isRequestingLoad}
                                />
                                <SummaryItem
                                    title="Total Net Weight"
                                    unit={settings.weightShortName}
                                    value={totalLoadCapacity}
                                    isLoading={isRequesting}
                                />
                                <SummaryItem
                                    title={`Avg Yield/${settings.areaShortName}`}
                                    value={avgYieldPerAcre}
                                    unit={settings.weightShortName}
                                    isLoading={isRequestingLoad}
                                />
                                <SummaryItem
                                    title={`Avg ${weightShortName}/Hour`}
                                    value={averageCapacityPerHour}
                                    isLoading={donutInfo && donutInfo.isRequesting ? donutInfo.isRequesting : isRequestingLoad}
                                />
                                <SummaryItem
                                    title="Yield By Truck"
                                    value={yieldByTruck}
                                    unit={settings.weightShortName}
                                    isLoading={isRequestingLoad}
                                />
                                <SummaryItem
                                    title="Avg Load Time"
                                    value={toDisplayTimeString(d.load.avg_load_time)}
                                    isLoading={isRequestingLoad}
                                />
                                <SummaryItem
                                    title="Avg Roundtrip Load Time"
                                    value={toDisplayTimeString(d.load.avg_roundtrip_load_time)}
                                    isLoading={isRequestingLoad}
                                />
                                {
                                    !isTareIncludedLoad && tareLoad ?
                                        <SummaryItem
                                            title="Total Tare Loads"
                                            value={d && d.tare && d.tare.total_loads}
                                            isLoading={isRequestingLoad}
                                        /> : null
                                }
                                <SummaryItem
                                    title="Total Tare Weight"
                                    value={totalTareWeight}
                                    unit={settings.weightShortName}
                                    isLoading={isRequestingLoad}
                                />
                                <SummaryItem
                                    title="Total Weight"
                                    value={totalWeight}
                                    unit={settings.weightShortName}
                                    isLoading={isRequestingLoad}
                                />
                                <SummaryItem
                                    title="Avg Tare Load Time"
                                    value={toDisplayTimeString(d && d.tare && d.tare.avg_load_time)}
                                    isLoading={isRequestingLoad}
                                />
                                <SummaryItem
                                    title="Avg Tare Roundtrip Load Time"
                                    value={toDisplayTimeString(d && d.tare && d.tare.avg_roundtrip_load_time)}
                                    isLoading={isRequestingLoad}
                                />
                            </SummaryRow>
                            <div className='mt-1'>
                                {(selectedPlanTypeId && selectedPlanTypeId === 2) ?
                                    <SeedVarietyRowItem dataBySeedVariety={dataBySeedVariety} isRequestingLoad={isRequestingLoad} settings={settings} />
                                    : null}
                            </div>
                        </>
                    }

                    {/* {isCrewView(view) && isManualLoad && d.load && <SummaryRow>
                <SummaryItem
                    title="Yield By Truck"
                    value={yieldByTruck}
                    unit={settings.weightShortName}
                    isLoading={isRequestingLoad}
                />
                <SummaryItem
                    title="Avg Roundtrip Load Time"
                    value={toDisplayTimeString(d.load.avg_roundtrip_load_time)}
                    isLoading={isRequestingLoad}
                />
            </SummaryRow>} */}
                    {isCrewView(view) && isAutomaticLoad && d.load && <SummaryRow>
                        <SummaryItem
                            title="Total Loads"
                            value={d.load.total_loads}
                            titleClassName="mb-3"
                            isLoading={isRequestingLoad}
                        />
                        <SummaryItem
                            title="Total Net Weight"
                            unit={settings.weightShortName}
                            value={totalLoadCapacity}
                            isLoading={isRequesting}
                        />
                        <SummaryItem
                            title="Avg Roundtrip Load Time"
                            value={toDisplayTimeString(d.load.avg_roundtrip_load_time)}
                            isLoading={isRequestingLoad}
                        />
                        <SummaryItem
                            title={`Avg ${weightShortName}/Hour`}
                            value={averageCapacityPerHour}
                            isLoading={donutInfo && donutInfo.isRequesting ? donutInfo.isRequesting : isRequestingLoad}
                        />
                    </SummaryRow>}
                    {/* {isHarvester && isAutomaticLoad && d.load && <SummaryRow>
                <SummaryItem
                    title="Total Loads"
                    value={d.load.total_loads}
                    titleClassName="mb-3"
                    isLoading={isRequestingLoad}
                />
                <SummaryItem
                    title="Avg Roundtrip Load Time"
                    value={toDisplayTimeString(d.load.avg_roundtrip_load_time)}
                    isLoading={isRequestingLoad}
                />
            </SummaryRow>} */}
                    {isHarvester && isManualLoad && d.load && <SummaryRow>
                        <SummaryItem
                            title="Number of Loads"
                            value={d.load.total_loads}
                            isLoading={isRequestingLoad}
                        />
                        <SummaryItem
                            title="Total Net Weight"
                            unit={settings.weightShortName}
                            value={totalLoadCapacity}
                            isLoading={isRequesting}
                        />
                        <SummaryItem
                            title={`Avg Yield/${settings.areaShortName}`}
                            unit={settings.weightShortName}
                            value={avgYieldPerAcre}
                            isLoading={isRequestingLoad}
                        />
                        <SummaryItem
                            title={`Avg ${weightShortName}/Hour`}
                            value={averageCapacityPerHour}
                            isLoading={donutInfo && donutInfo.isRequesting ? donutInfo.isRequesting : isRequestingLoad}
                        />
                        <SummaryItem
                            title="Avg Load Time"
                            value={toDisplayTimeString(d.load.avg_load_time)}
                            isLoading={isRequestingLoad}
                        />
                        {
                            !isTareIncludedLoad && tareLoad ?
                                <SummaryItem
                                    title="Total Tare Loads"
                                    value={d && d.tare && d.tare.total_loads}
                                    isLoading={isRequestingLoad}
                                /> : null
                        }
                        <SummaryItem
                            title="Total Tare Weight"
                            value={totalTareWeight}
                            unit={settings.weightShortName}
                            isLoading={isRequestingLoad}
                        />
                        <SummaryItem
                            title="Total Weight"
                            value={totalWeight}
                            unit={settings.weightShortName}
                            isLoading={isRequestingLoad}
                        />
                        <SummaryItem
                            title="Avg Tare Load Time"
                            value={toDisplayTimeString(d && d.tare && d.tare.avg_load_time)}
                            isLoading={isRequestingLoad}
                        />
                        <SummaryItem
                            title="Avg Tare Roundtrip Load Time"
                            value={toDisplayTimeString(d && d.tare && d.tare.avg_roundtrip_load_time)}
                            isLoading={isRequestingLoad}
                        />
                    </SummaryRow>}
                    {isEGView(view) && speed.payload.is_speed &&
                        <>
                            <SummaryRow>
                                {isHarvester && isManualLoad && d.load && <SummaryItem
                                    title="Avg Roundtrip Load Time"
                                    value={toDisplayTimeString(d.load.avg_roundtrip_load_time)}
                                    isLoading={isRequestingLoad}
                                />}
                                <SummaryItem
                                    title={`Avg Speed (${settings.speedShortName})`}
                                    value={unit.toRoundOfSpeed(speed.payload.avg_speed)}
                                    isLoading={isRequestingSpeed}
                                />
                                <SummaryItem
                                    title={`Max Speed (${settings.speedShortName})`}
                                    value={unit.toRoundOfSpeed(speed.payload.max_speed)}
                                    isLoading={isRequestingSpeed}
                                />
                            </SummaryRow>
                            <div className='mt-1'>
                                {(selectedPlanTypeId && selectedPlanTypeId === 2) ?
                                    <SeedVarietyRowItem dataBySeedVariety={dataBySeedVariety} isRequestingLoad={isRequestingLoad} settings={settings} />
                                    : null}
                            </div>
                        </>
                    }
                    {/* {d.tare && (showTareLoad || (isFarmView(view) && d.tare.total_loads && d.tare.total_loads > 0)) ? <SummaryRow>
                        <SummaryItem
                            title="Tare Loads"
                            value={d.tare.total_loads}
                            isLoading={isRequestingLoad}
                        />
                        <SummaryItem
                            title="Avg Roundtrip Tare Load Time"
                            value={toDisplayTimeString(d.tare.avg_roundtrip_load_time)}
                            isLoading={isRequestingLoad}
                        />
                        <SummaryItem
                            title="Avg Tare Load Time"
                            value={toDisplayTimeString(d.tare.avg_load_time)}
                            isLoading={isRequestingLoad}
                        />
                    </SummaryRow> : null} */}
                </SummaryBox>
        }
    </div>;
};

const SeedVarietyRowItem = ({ dataBySeedVariety, isRequestingLoad, settings }) => {
    if (dataBySeedVariety && dataBySeedVariety.length > 0) {
        const newData = dataBySeedVariety.map((varietyData, index) => {
            return (<AnalyzeSeedAccordian key={index} idx={index} isRequestingLoad={isRequestingLoad}
                varietyData={varietyData} settings={settings} />)
        })
        return (newData);
    }
    return (<></>)
}

export default SummaryOne;